import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAppSelector } from '../../../../../../../app/store';
import { useUpsertDrgMutation, useDeleteDrgMutation } from '../../../../../../../services/ContractService';
import { DrgView } from '../../../../../../../services/ContractService/types/contractTypes';

interface DrgFormData {
    code: string;
    title: string;
    weight: number;
    arithmeticMeanLOS: number;
    grouperCode: string;
}

interface UseDrgFormProps {
    editingDrg: DrgView | null;
    feeScheduleId: string;
    open: boolean;
    onClose: () => void;
}

interface UseDrgFormResult {
    formData: DrgFormData;
    formErrors: { [key: string]: string };
    isSaving: boolean;
    isDeleting: boolean;
    isLoading: boolean;
    availableGroupers: ReturnType<typeof useAppSelector> extends { contract: { currentFeeSchedule: { groupers: infer T } } } ? T : any[];
    calculatedRate: number;
    selectedFeeSchedule: ReturnType<typeof useAppSelector> extends { contract: { currentFeeSchedule: infer T } } ? T : any;
    handleFormChange: (field: keyof DrgFormData, value: string | number) => void;
    handleSave: () => Promise<void>;
    handleDelete: () => Promise<void>;
}

const generateTempId = () => `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const initialFormData: DrgFormData = {
    code: '',
    title: '',
    weight: 0,
    arithmeticMeanLOS: 0,
    grouperCode: '',
};

export const useDrgForm = ({
    editingDrg,
    feeScheduleId,
    open,
    onClose
}: UseDrgFormProps): UseDrgFormResult => {
    const selectedFeeSchedule = useAppSelector((state) => state.contract.currentFeeSchedule);
    const [formData, setFormData] = useState<DrgFormData>(initialFormData);
    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

    const [upsertDrg, { isLoading: isSaving }] = useUpsertDrgMutation();
    const [deleteDrg, { isLoading: isDeleting }] = useDeleteDrgMutation();

    const isLoading = isSaving || isDeleting;

    const availableGroupers = useMemo(() => 
        selectedFeeSchedule?.groupers || [], 
        [selectedFeeSchedule?.groupers]
    );

    const calculatedRate = useMemo(() => {
        if (!selectedFeeSchedule?.groupers || !formData.grouperCode || !formData.weight) {
            return 0;
        }

        const matchingGrouper = selectedFeeSchedule.groupers.find(
            grouper => grouper.code === formData.grouperCode
        );

        if (!matchingGrouper || !matchingGrouper.rate) {
            return 0;
        }

        return matchingGrouper.rate * formData.weight;
    }, [selectedFeeSchedule?.groupers, formData.grouperCode, formData.weight]);

    useEffect(() => {
        if (editingDrg) {
            setFormData({
                code: editingDrg.code,
                title: editingDrg.title,
                weight: editingDrg.weight,
                arithmeticMeanLOS: editingDrg.arithmeticMeanLOS,
                grouperCode: editingDrg.grouperCode,
            });
        } else {
            setFormData(initialFormData);
        }
        setFormErrors({});
    }, [editingDrg, open]);

    const handleFormChange = useCallback((field: keyof DrgFormData, value: string | number) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (formErrors[field]) {
            setFormErrors(prev => ({ ...prev, [field]: '' }));
        }
    }, [formErrors]);

    const validateForm = useCallback(() => {
        const errors: { [key: string]: string } = {};

        if (!formData.code.trim()) {
            errors.code = 'Code is required';
        }

        if (!formData.title.trim()) {
            errors.title = 'Title is required';
        }

        if (formData.weight <= 0) {
            errors.weight = 'Weight must be greater than 0';
        }

        if (formData.arithmeticMeanLOS < 0) {
            errors.arithmeticMeanLOS = 'Arithmetic Mean LOS cannot be negative';
        }

        if (!formData.grouperCode.trim()) {
            errors.grouperCode = 'Grouper code is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }, [formData]);

    const handleSave = useCallback(async () => {
        if (!validateForm()) return;

        try {
            const drgData: DrgView = {
                id: editingDrg?.id || generateTempId(),
                feeScheduleId,
                code: formData.code.trim(),
                title: formData.title.trim(),
                weight: formData.weight,
                arithmeticMeanLOS: formData.arithmeticMeanLOS,
                grouperCode: formData.grouperCode.trim(),
                rate: 0,
                drgType: 'Medical'
            };

            await upsertDrg({ drg: drgData }).unwrap();
            onClose();
        } catch (error) {
            console.error('Error saving DRG:', error);
        }
    }, [validateForm, editingDrg, feeScheduleId, formData, upsertDrg, onClose]);

    const handleDelete = useCallback(async () => {
        if (!editingDrg?.id) return;

        const confirmDelete = window.confirm(`Are you sure you want to delete DRG "${editingDrg.code}"?`);
        if (!confirmDelete) return;

        try {
            await deleteDrg({
                id: editingDrg.id,
                feeScheduleId: editingDrg.feeScheduleId
            }).unwrap();
            onClose();
        } catch (error) {
            console.error('Error deleting DRG:', error);
        }
    }, [editingDrg, deleteDrg, onClose]);

    return {
        formData,
        formErrors,
        isSaving,
        isDeleting,
        isLoading,
        availableGroupers,
        calculatedRate,
        selectedFeeSchedule,
        handleFormChange,
        handleSave,
        handleDelete
    };
};
